
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

// JSON-safe audit details type - prevents non-serializable values
type AuditDetails = Record<string, string | number | boolean | null>;

export class AuditService {
  // Log user actions for audit trail
  static async logAction(
    action: string,
    entityType: string,
    entityId?: string,
    details?: AuditDetails
  ) {
    try {
      // Skip audit RPC for unauthenticated visitors — the underlying RLS
      // requires an authenticated role and would return 401, which leaks into
      // the browser console for every guest pageview.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return;
      }

      await supabase.rpc('log_user_action', {
        p_action: action,
        p_entity_type: entityType,
        p_entity_id: entityId ?? null,
        p_details: details ? JSON.stringify(details) : null,
      });
      logger.log(`Audit log: ${action} on ${entityType}`, { entityId, details });
    } catch (error) {
      // Structured logging with full context for observability
      logger.error('Audit log failed', { 
        action, 
        entityType, 
        entityId, 
        details,
        error: error instanceof Error ? error.message : String(error)
      });
      // In development, surface audit failures immediately
      if (import.meta.env.DEV) {
        throw error;
      }
    }
  }

  // Specific logging methods for common actions
  static async logBookAction(action: string, bookId: string, details?: AuditDetails) {
    await this.logAction(action, 'book', bookId, details);
  }

  static async logOrderAction(action: string, orderId: string, details?: AuditDetails) {
    await this.logAction(action, 'order', orderId, details);
  }

  static async logUserAction(action: string, userId?: string, details?: AuditDetails) {
    await this.logAction(action, 'user', userId, details);
  }

  static async logAdminAction(action: string, targetType: string, targetId?: string, details?: AuditDetails) {
    await this.logAction(action, targetType, targetId, details);
  }

  static async logPaymentAction(action: string, transactionId: string, details?: AuditDetails) {
    await this.logAction(action, 'payment', transactionId, details);
  }
}

// Export type for consumers
export type { AuditDetails };
